(ns frontend.ui.component.lp-portal.report.body.real-estate.fund.hotel-v2.controller
  
  (:require
   [clojure.string :as string]
   [common.util :as cu]
   [frontend.ui.component.chart.utils :as chart-utils]
   [frontend.ui.component.lp-portal.donut-chart.view :as donut-chart]
   [frontend.ui.component.lp-portal.report.body.invest-europe.holding.controller :refer [invest-europe-editable-data->raw-data
                                                                                         invest-europe-table-raw-data->editable-data]]
   [frontend.ui.component.lp-portal.report.body.real-estate.fund.common.controller :refer [editable-data->raw-data]]
   [frontend.ui.component.lp-portal.report.component.multi-chart.controller :as multi-controller]
   [frontend.ui.component.lp-portal.report.component.waterfall-chart.controller :as waterfall-chart-ctl]
   [frontend.ui.component.lp-portal.report.controller :as report-ctl]
   [frontend.utils :as fu]))

(defn- fair-value-by-country
  [{:keys [labels country]}]
  (map vector labels country))

(defn- fair-value-by-location
  [{:keys [labels location]}]
  (map vector labels location))

(defn- fair-value-by-type
  [{:keys [labels type]}]
  (map vector labels type))

(defn- rooms-by-country
  [{:keys [labels country]}]
  (map vector labels country))

(defn- rooms-by-location
  [{:keys [labels location]}]
  (map vector labels location))

(defn- rooms-by-operator
  [{:keys [labels operator]}]
  (map vector labels operator))

(defn- pdf-image-key
  [image-key]
  (str "hotel-v2-image-" (name image-key)))

(defn- resolve-image-src
  [images image-value]
  (if (integer? image-value)
    (or (get-in images [image-value :download-url]) "")
    image-value))

(defn- absolute-image-src
  [src]
  (cond
    (cu/blank-value? src)
    src

    (or (string/starts-with? src "http://")
        (string/starts-with? src "https://")
        (string/starts-with? src "data:"))
    src

    (string/starts-with? src "/")
    (str (.-origin js/document.location) src)

    :else
    src))

(defn- image-content
  [{:keys [images] :as props} image-key]
  (let [image-data (report-ctl/image-content-resi-v2
                    props
                    image-key
                    (get-in props [:dynamic-report-images :image-id image-key]))]
    (assoc image-data
           :image (resolve-image-src images (:image image-data))
           :pdf-image-key (pdf-image-key image-key))))

(defn- pdf-images
  [{:keys [dynamic-report-images images]}]
  (->> (get dynamic-report-images :image-id)
       (keep (fn [[image-key image-value]]
               (let [resolved-image (resolve-image-src images image-value)]
                 (when-not (cu/blank-value? resolved-image)
                   [(pdf-image-key image-key) (absolute-image-src resolved-image)]))))
       (into {})))

(defn contact-images
  [{:keys [report-data] :as props}]
  (let [column-count (min (count (get-in report-data [:contact-information :column-data-types])) 3)]
    (into {}
          (map (fn [idx]
                 (let [key (keyword (str "contact-" (inc idx)))
                       image-key (keyword (str "contact-image-" (inc idx)))]
                   [key (image-content props image-key)]))
               (range column-count)))))

(defn contact-table-data
  [{:keys [report-data] :as _props}]
  (let [column-data-types-max-5 (take 5 (get-in report-data [:contact-information :column-data-types]))
        new-data (assoc-in report-data [:contact-information :column-data-types] column-data-types-max-5)]
    (:contact-information new-data)))

(defn snapshot-raw-data->editable-data
  [raw-data]
  {:columns ["Label" "Value"]
   :highlight-figures? true
   :data    (->> raw-data
                 :data
                 (mapv (fn [row]
                         (vec (concat [(:row-key row)] (:row-data row))))))
   :cell-data-types (->> raw-data
                         :data
                         (mapv :cell-data-types))
   :cell-styles (->> raw-data
                     :data
                     (mapv :cell-styles))})

(defn ^{:jsdoc ["@nocollapse"]} custom-donut-legend-formatter
  []
  (this-as
   js-data-point
   (let [point (js->clj js-data-point
                        :keywordize-keys
                        true)
         legend-name (fu/aget-in point ["name"])]
     (str legend-name))))

(defn ->ui-data
  [{:keys [_dynamic-data _dynamic-report-images report-data] :as props}]
  {:fund-name                                (:fund-name report-data)
   :pdf-images                               (pdf-images props)
   :pdf-cover-image                          (image-content
                                              props
                                              :pdf-cover-image)
   :pdf-toc-image                            (image-content
                                              props
                                              :pdf-toc-image)
   :reference-date                           (get-in report-data
                                                     [:reference-date])
   :reporting-period                         (:reporting-period
                                              report-data)

   :fund-summary                             (report-ctl/with-footnote
                                               props
                                               :fund-summary-footnotes
                                               (:fund-summary
                                                report-data))
   :contact-images                           (contact-images
                                              props)
   :contact-information                      (report-ctl/with-footnote
                                               props
                                               nil
                                               (contact-table-data props))
   :valuation-details                        (report-ctl/with-footnote
                                               props
                                               :valuation-details-footnotes
                                               (:valuation-details
                                                report-data))
   :key-metrics                              (report-ctl/with-footnote
                                               props
                                               :key-metrics-footnotes
                                               (:key-metrics
                                                report-data))
   :fund-capital-metrics                     (report-ctl/with-footnote
                                               props
                                               :fund-capital-metrics-footnotes
                                               (:fund-capital-metrics report-data)) 
   :fund-kpis-metrics                        (report-ctl/with-footnote
                                               props
                                               :fund-kpis-metrics-footnotes
                                               (:fund-kpis-metrics report-data)) 
   :performance-metrics                      (report-ctl/with-footnote
                                               props
                                               :performance-metrics-footnotes
                                               (:performance-metrics report-data)) 
   :portfolio-metrics                        (report-ctl/with-footnote
                                               props
                                               :portfolio-metrics-footnotes
                                               (:portfolio-metrics report-data))
   :portfolio-snapshot                       (->> (report-ctl/with-footnote props
                                                    nil
                                                    (get-in report-data [:portfolio-snapshot]))
                                                  (report-ctl/with-editable-tables
                                                    (merge
                                                     {:fixed-columns? true
                                                      :fixed-labels? true
                                                      :fixed-rows? true} props)
                                                    :portfolio-snapshot
                                                    snapshot-raw-data->editable-data
                                                    editable-data->raw-data))
   :financial-snapshot                       (->> (report-ctl/with-footnote props
                                                    nil
                                                    (get-in report-data [:financial-snapshot]))
                                                  (report-ctl/with-editable-tables
                                                    (merge
                                                     {:fixed-columns? true
                                                      :fixed-labels? true
                                                      :fixed-rows? true} props)
                                                    :financial-snapshot
                                                    snapshot-raw-data->editable-data
                                                    editable-data->raw-data))
   :esg-snapshot                             (->> (report-ctl/with-footnote props
                                                    nil
                                                    (get-in report-data [:esg-snapshot]))
                                                  (report-ctl/with-editable-tables 
                                                    (merge
                                                     {:fixed-columns? true
                                                      :fixed-labels? true
                                                      :fixed-rows? true} props) 
                                                    :esg-snapshot
                                                    snapshot-raw-data->editable-data
                                                    editable-data->raw-data))
   :quarter-update-image-1                   (image-content
                                              props
                                              :quarter-update-image-1)
   :quarter-update-image-2                   (image-content
                                              props
                                              :quarter-update-image-2)
   :market-overview                          (->> (report-ctl/with-footnote
                                                    props
                                                    :market-overview-footnotes
                                                    (:market-overview report-data))
                                                  (report-ctl/with-editable-tables 
                                                    (merge {:fixed-rows? true} props)
                                                    :market-overview
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :market-overview-image                    (image-content
                                              props
                                              :market-overview-image)
   :fund-returns-table                       (report-ctl/with-footnote
                                               props
                                               :fund-returns-table-footnotes
                                               (:fund-returns-table
                                                report-data))
   :benchmark-index                          (report-ctl/with-footnote
                                               props
                                               :benchmark-index-footnotes
                                               (:benchmark-index
                                                report-data))
   :nav-per-unit                             (report-ctl/with-footnote
                                               props
                                               :nav-per-unit-footnotes
                                               (multi-controller/multi-chart-ctl-capman
                                                (:nav-per-unit
                                                 report-data)
                                                {:custom-linewidth 4}))
   :nav-bridge                               (report-ctl/with-footnote
                                               props
                                               :nav-bridge-footnotes
                                               (waterfall-chart-ctl/chart-config-capman
                                                (:nav-bridge
                                                 report-data)
                                                report-data))
   :debt-kpis                                (report-ctl/with-footnote
                                               props
                                               :debt-kpis-footnotes
                                               (:debt-kpis
                                                report-data))
   :debt-facilities                          (report-ctl/with-footnote
                                               props
                                               :debt-facilities-footnotes
                                               (:debt-facilities
                                                report-data))
   :loan-to-gav                              (report-ctl/with-footnote
                                               props
                                               :loan-to-gav-footnotes
                                               (multi-controller/multi-chart-ctl-capman
                                                (:loan-to-gav
                                                 report-data)
                                                {:custom-y-axis-formatter chart-utils/axis-pct-formatter-0-decimals}))
   :rent-maturities                          (report-ctl/with-footnote
                                               props
                                               nil
                                               (multi-controller/multi-chart-ctl-capman
                                                (:rent-maturities
                                                 report-data)))
   :portfolio-summary                        (report-ctl/with-footnote
                                               props
                                               :portfolio-summary-footnotes
                                               (:portfolio-summary
                                                report-data))
   :portfolio-noi-and-niy                    (report-ctl/with-footnote
                                               props
                                               nil
                                               (multi-controller/multi-chart-ctl-capman
                                                (:portfolio-noi-and-niy
                                                 report-data)))
   :valuation-change                         (report-ctl/with-footnote
                                               props
                                               :valuation-change-footnotes
                                               (waterfall-chart-ctl/chart-config-capman
                                                (:valuation-change
                                                 report-data)
                                                report-data))
   :fair-value-by-country                    (report-ctl/with-footnote
                                               props
                                               nil
                                               (donut-chart/donut-chart-config
                                                (fair-value-by-country
                                                 (:chart-data-fair-value-by-country
                                                  report-data))
                                                {:data-label-formatter
                                                 chart-utils/pie-%-label-formatter-no-letters-0-decimals
                                                 :resi-custom-colors? true
                                                 :legend-formatter-fn custom-donut-legend-formatter
                                                 :preserve-order? true}))
   :fair-value-by-location                   (report-ctl/with-footnote
                                               props
                                               nil
                                               (donut-chart/donut-chart-config
                                                (fair-value-by-location
                                                 (:chart-data-fair-value-by-location
                                                  report-data))
                                                {:data-label-formatter
                                                 chart-utils/pie-%-label-formatter-no-letters-0-decimals
                                                 :resi-custom-colors? true
                                                 :legend-formatter-fn custom-donut-legend-formatter
                                                 :preserve-order? true}))
   :fair-value-by-type                       (report-ctl/with-footnote
                                               props
                                               nil
                                               (donut-chart/donut-chart-config
                                                (fair-value-by-type
                                                 (:chart-data-fair-value-by-type
                                                  report-data))
                                                {:data-label-formatter
                                                 chart-utils/pie-%-label-formatter-no-letters-0-decimals
                                                 :resi-custom-colors? true
                                                 :legend-formatter-fn custom-donut-legend-formatter
                                                 :preserve-order? true}))
   :rooms-by-country                         (report-ctl/with-footnote
                                               props
                                               nil
                                               (donut-chart/donut-chart-config
                                                (rooms-by-country
                                                 (:chart-data-rooms-by-country
                                                  report-data))
                                                {:data-label-formatter
                                                 chart-utils/pie-float-formatter-0-decimals
                                                 :resi-custom-colors? true
                                                 :legend-formatter-fn custom-donut-legend-formatter
                                                 :preserve-order? true}))
   :rooms-by-location                        (report-ctl/with-footnote
                                               props
                                               nil
                                               (donut-chart/donut-chart-config
                                                (rooms-by-location
                                                 (:chart-data-rooms-by-location
                                                  report-data))
                                                {:data-label-formatter
                                                 chart-utils/pie-float-formatter-0-decimals
                                                 :resi-custom-colors? true
                                                 :legend-formatter-fn custom-donut-legend-formatter
                                                 :preserve-order? true}))
   :rooms-by-operator                        (report-ctl/with-footnote
                                               props
                                               nil
                                               (donut-chart/donut-chart-config
                                                (rooms-by-operator
                                                 (:chart-data-rooms-by-operator
                                                  report-data))
                                                {:data-label-formatter
                                                 chart-utils/pie-float-formatter-0-decimals
                                                 :resi-custom-colors? true
                                                 :legend-formatter-fn custom-donut-legend-formatter
                                                 :preserve-order? true}))
   :acquisitions                             (report-ctl/with-footnote
                                               props
                                               :acquisitions-footnotes
                                               (:acquisitions
                                                report-data))
   :dispositions                             (report-ctl/with-footnote
                                               props
                                               :dispositions-footnotes
                                               (:dispositions
                                                report-data))
   :investment-activity-image-1              (image-content
                                              props
                                              :investment-activity-image-1)
   :investment-activity-image-2              (image-content
                                              props
                                              :investment-activity-image-2)
   :gresb-by-year                            (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:gresb-by-year report-data))
                                                  (report-ctl/with-editable-tables props :gresb-by-year
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :gresb-logo-image                         (image-content
                                              props
                                              :gresb-logo-image)
   :portfolio-epc                            (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:portfolio-epc report-data))
                                                  (report-ctl/with-editable-tables props :portfolio-epc
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :green-certs-by-country                   (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:green-certs-by-country report-data))
                                                  (report-ctl/with-editable-tables props :green-certs-by-country
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :green-certificate-image-1                (image-content
                                              props
                                              :green-certificate-image-1)
   :green-certificate-image-2                (image-content
                                              props
                                              :green-certificate-image-2)
   :esg-investments-image                    (image-content
                                              props
                                              :esg-investments-image)
   :share-of-fossil-free-energy              (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:share-of-fossil-free-energy report-data))
                                                  (report-ctl/with-editable-tables props :share-of-fossil-free-energy
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :emissions                                (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:emissions report-data))
                                                  (report-ctl/with-editable-tables props :emissions
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :annual-carbon-intensity                   (->>
                                               (get-in report-data
                                                       [:annual-carbon-intensity])
                                               (report-ctl/with-footnote
                                                 props
                                                 nil)
                                               (report-ctl/with-editable-charts
                                                 (merge {:multi-chart? true} props)
                                                 :annual-carbon-intensity
                                                 multi-controller/multi-chart-ctl-capman
                                                 multi-controller/config->editable-data
                                                 multi-controller/editable-data->raw-data))
   
   :cmrf-towards-net-zero                    (->>
                                              (get-in report-data
                                                      [:cmrf-towards-net-zero])
                                              (report-ctl/with-footnote
                                                props
                                                nil)
                                              (report-ctl/with-editable-charts
                                                (merge {:multi-chart? true} props)
                                                :cmrf-towards-net-zero
                                                multi-controller/multi-chart-ctl-capman
                                                multi-controller/config->editable-data
                                                multi-controller/editable-data->raw-data))
   :esg-nps                                  (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:esg-nps report-data))
                                                  (report-ctl/with-editable-tables props :esg-nps
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :crrem-logo-image                         (image-content
                                              props
                                              :crrem-logo-image)
   :nav-breakdown-ifrs                       (report-ctl/with-footnote
                                               props
                                               :nav-breakdown-ifrs-footnotes
                                               (:nav-breakdown-ifrs
                                                report-data))
   :nav-breakdown-inrev                      (report-ctl/with-footnote
                                               props
                                               :nav-breakdown-inrev-footnotes
                                               (:nav-breakdown-inrev
                                                report-data))
   :income-statement                         (report-ctl/with-footnote
                                               props
                                               :income-statement-footnotes
                                               (:income-statement
                                                report-data))
   :financial-positions-statement-1          (report-ctl/with-footnote
                                               props
                                               :financial-positions-statement-1-footnotes
                                               (:financial-positions-statement-1
                                                report-data))
   :financial-positions-statement-2          (report-ctl/with-footnote
                                               props
                                               :financial-positions-statement-2-footnotes
                                               (:financial-positions-statement-2
                                                report-data))
   :cash-flows-statement-1                   (report-ctl/with-footnote
                                               props
                                               :cash-flows-statement-1-footnotes
                                               (:cash-flows-statement-1
                                                report-data))
   :cash-flows-statement-2                   (report-ctl/with-footnote
                                               props
                                               :cash-flows-statement-2-footnotes
                                               (:cash-flows-statement-2
                                                report-data))
   :portfolio-denmark                        (report-ctl/with-footnote
                                               props
                                               :portfolio-denmark-footnotes
                                               (:portfolio-denmark
                                                report-data))
   :portfolio-finland                        (report-ctl/with-footnote
                                               props
                                               :portfolio-finland-footnotes
                                               (:portfolio-finland
                                                report-data))
   :portfolio-norway                         (report-ctl/with-footnote
                                              props
                                              :portfolio-norway-footnotes
                                              (:portfolio-norway
                                               report-data))
   :portfolio-sweden                         (report-ctl/with-footnote
                                               props
                                               :portfolio-sweden-footnotes
                                               (:portfolio-sweden
                                                report-data))
   :glossary                                 (report-ctl/with-footnote
                                               props
                                               :glossary-footnotes
                                               (:glossary
                                                report-data))

   ;; --- Rich content blocks ---
   :strategy                                 (report-ctl/rich-content
                                              props
                                              :strategy)
   :portfolio-update-text                    (report-ctl/rich-content
                                              props
                                              :portfolio-update-text)
   :investment-activity-and-pipeline-text    (report-ctl/rich-content
                                              props
                                              :investment-activity-and-pipeline-text)
   :performance-and-outlook-text             (report-ctl/rich-content
                                              props
                                              :performance-and-outlook-text)
   :esg-summary-text                         (report-ctl/rich-content
                                              props
                                              :esg-summary-text)
   :gresb-text                               (report-ctl/rich-content
                                              props
                                              :gresb-text)
   :epc-text                                 (report-ctl/rich-content
                                              props
                                              :epc-text)
   :eu-taxonomy-text                         (report-ctl/rich-content
                                              props
                                              :eu-taxonomy-text)
   :green-certificates-text                  (report-ctl/rich-content
                                              props
                                              :green-certificates-text)
   :green-electricity-text                   (report-ctl/rich-content
                                              props
                                              :green-electricity-text)
   :esg-investments-text                     (report-ctl/rich-content
                                              props
                                              :esg-investments-text)
   :crrem-pathway-text                       (report-ctl/rich-content
                                              props
                                              :crrem-pathway-text)
   :tenant-satisfaction-text                 (report-ctl/rich-content
                                              props
                                              :tenant-satisfaction-text)
   :ungc-text                                (report-ctl/rich-content
                                              props
                                              :ungc-text)
   :disclaimer-text                          (report-ctl/rich-content
                                              props
                                              :disclaimer-text)})

(def ^:private v3-donut-charts
  "The six donut charts v3 renders as percentages and lets the editor rename.
   Each entry maps the ui-data key to the report-data key and the fn that pairs
   its labels with values."
  [[:fair-value-by-country  :chart-data-fair-value-by-country  fair-value-by-country]
   [:fair-value-by-location :chart-data-fair-value-by-location fair-value-by-location]
   [:fair-value-by-type     :chart-data-fair-value-by-type     fair-value-by-type]
   [:rooms-by-country       :chart-data-rooms-by-country       rooms-by-country]
   [:rooms-by-location      :chart-data-rooms-by-location      rooms-by-location]
   [:rooms-by-operator      :chart-data-rooms-by-operator      rooms-by-operator]])

(defn- v3-donut-chart
  [{:keys [report-data] :as props} [ui-key data-key labels-fn]]
  [ui-key
   (->> (donut-chart/donut-chart-config
         (labels-fn (get report-data data-key))
         {:data-label-formatter chart-utils/pie-%-label-formatter-no-letters-0-decimals
          :resi-custom-colors? true
          :legend-formatter-fn custom-donut-legend-formatter
          :preserve-order? true})
        (report-ctl/with-footnote props nil)
        (report-ctl/with-custom-title props ui-key))])

(defn ->ui-data-v3
  "Hotels v3 ui-data. Identical to v2 except that every donut chart labels its
   slices as percentages -- v2 shows raw room counts on the three room charts --
   and each is renameable from the report editor."
  [props]
  (->> v3-donut-charts
       (map (partial v3-donut-chart props))
       (into {})
       (merge (->ui-data props))))
